<script lang="ts">
  import type { Achievement } from '$lib/achievements'
  import { confetti } from '$lib/attachments/party-effect'
  import { play } from '$lib/sound'
  import { onMount } from 'svelte'
  import { expoOut, quintIn } from 'svelte/easing'
  import { fly, type TransitionConfig } from 'svelte/transition'
  import BadgeIcon from '~icons/tabler/badge'
  import BadgesIcon from '~icons/tabler/badges'
  import MedalIcon from '~icons/tabler/medal-2'
  import TrophyIcon from '~icons/tabler/trophy'
  import Flash from './effects/Flash.svelte'
  import GodRays from './effects/GodRays.svelte'
  import LightMotes from './effects/LightMotes.svelte'
  import { prefersReducedMotion } from './effects/motion'
  import { shake } from './effects/shake'
  import ShineSweep from './effects/ShineSweep.svelte'
  import ShockwaveRing from './effects/ShockwaveRing.svelte'

  interface Props {
    achievement: Achievement
    onContinue: () => void
  }

  let { achievement, onContinue }: Props = $props()

  let celebrated = $derived(
    achievement.rarity === 'epic' || achievement.rarity === 'legendary',
  )

  let slammed = $state(false)
  let impact = $state(false)
  let actionsReady = $state(false)

  onMount(() => {
    const reduced = prefersReducedMotion()
    const slamTimer = setTimeout(() => (slammed = true), reduced ? 0 : 350)
    const impactTimer = setTimeout(
      () => {
        impact = true
        play('achievement-new')
        if (celebrated) setTimeout(() => play('cheering'), 300)
      },
      reduced ? 100 : 850,
    )
    const actionsTimer = setTimeout(
      () => (actionsReady = true),
      reduced ? 400 : 2000,
    )
    return () => {
      clearTimeout(slamTimer)
      clearTimeout(impactTimer)
      clearTimeout(actionsTimer)
    }
  })

  // The badge slams down from the viewer: a slow approach that accelerates
  // into the impact.
  const badgeSlam = (_: Element): TransitionConfig => ({
    duration: 500,
    css: (t: number) => `
      transform: scale(${4 - 3 * quintIn(t)});
      opacity: ${Math.min(1, t * 4)};
    `,
  })
</script>

<div class="scene" {@attach shake(impact)}>
  <LightMotes mode="float" count={14} />
  {#if impact}
    <GodRays intense={celebrated} />
    <Flash />
    <ShockwaveRing />
  {/if}

  <div class="center">
    {#if slammed}
      <div class="badge" in:badgeSlam>
        <div class="face">
          {#if achievement.rarity === 'rare'}
            <BadgesIcon />
          {:else if achievement.rarity === 'epic'}
            <MedalIcon />
          {:else if achievement.rarity === 'legendary'}
            <TrophyIcon />
          {:else}
            <BadgeIcon />
          {/if}
        </div>
        {#if impact}
          <ShineSweep delay={350} />
          <div class="burst-anchor" {@attach confetti}></div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Anchored below the centerpiece so appearing content never reflows it. -->
  <div class="lower">
    {#if impact}
      <div class="caption">
        <div
          class="kicker"
          in:fly={{ y: 12, duration: 350, delay: 150, easing: expoOut }}
        >
          Achievement unlocked
        </div>
        <h2
          class="name"
          in:fly={{ y: 24, duration: 500, delay: 300, easing: expoOut }}
        >
          {achievement.name}
        </h2>
        <p
          class="description"
          in:fly={{ y: 16, duration: 400, delay: 450, easing: expoOut }}
        >
          {achievement.description}
        </p>
      </div>
    {/if}

    {#if actionsReady}
      <div class="actions" in:fly={{ y: 16, duration: 350, easing: expoOut }}>
        <button class="@button" onclick={onContinue}>Continue</button>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .scene {
    position: absolute;
    inset: 0;
  }
  .center {
    display: grid;
    position: absolute;
    place-items: center;
    inset: 0;
    pointer-events: none;
  }
  .lower {
    display: flex;
    position: absolute;
    /* Starts below the badge and its glow, whatever appears here. */
    top: calc(50% + min(24vmin, 7.5rem) + 2rem);
    right: 0;
    left: 0;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  .badge {
    /* A flat medal: solid saturated ring (the stage's --reveal-ring raw
       rarity anchor), flat face, hairline inner ring. No gradients. */
    display: grid;
    position: relative;
    place-items: center;
    border: 0.5rem solid var(--reveal-ring);
    border-radius: 50%;
    box-shadow: 0 0 2.5rem
      color-mix(in oklab, var(--reveal-ring) 40%, transparent);
    background: oklch(20% 0.02 270);
    aspect-ratio: 1;
    width: min(45vmin, 13rem);
    &::after {
      position: absolute;
      inset: 0.5rem;
      border: 2px solid color-mix(in oklab, var(--reveal-ring) 45%, transparent);
      border-radius: 50%;
      content: '';
    }
  }
  .face {
    color: oklch(98% 0.005 270);
    font-size: min(20vmin, 6rem);
    & :global(svg) {
      display: block;
    }
  }
  .burst-anchor {
    position: absolute;
    top: 20%;
    left: 50%;
    width: 1px;
    height: 1px;
  }
  .caption {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.625rem;
    max-width: min(80vw, 28rem);
    text-align: center;
  }
  .kicker {
    color: color-mix(in oklab, var(--reveal-hue) 80%, white);
    font-weight: var(--weight-bold);
    font-size: var(--scale-000);
    letter-spacing: 0.25em;
    text-shadow: 0 0 1.5rem var(--reveal-hue);
    text-transform: uppercase;
  }
  .name {
    color: oklch(97% 0.005 270);
    font-weight: var(--weight-bold);
    font-size: var(--scale-4);
    line-height: 1.1;
  }
  .description {
    color: oklch(78% 0.01 270);
    font-size: var(--scale-0);
  }
  .actions {
    margin-top: 0.5rem;
  }
</style>
